import React, { useState, useEffect } from 'react';
import Header from '../components/landing/Header';
import TopBar from '../components/landing/TopBar';
import Footer from '../components/landing/Footer';
import api from '../utils/axios';
import getFullUrl from '../utils/formatUrl';
import { Maximize2, X } from 'lucide-react';

const Gallery = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [content, setContent] = useState({});
    // const [galleryImages, setGalleryImages] = useState([]); // Removed because it's set but not used
    const [galleryAlbums, setGalleryAlbums] = useState([]);
    const [currentCategory, setCurrentCategory] = useState(null);
    const [currentAlbum, setCurrentAlbum] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        const handleScroll = () => setIsScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        fetchContent();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const fetchContent = async () => {
        try {
            const res = await api.get('/content');
            const contentMap = {};
            res.data.data.forEach(item => {
                contentMap[item.key] = item.value;
            });
            setContent(contentMap);
            
            /*
            if (contentMap.gallery_images) {
                try {
                    setGalleryImages(JSON.parse(contentMap.gallery_images));
                } catch (e) {
                    console.error("Failed to parse gallery images", e);
                }
            }
            */

            if (contentMap.gallery_albums) {
                try {
                    const data = JSON.parse(contentMap.gallery_albums);
                    // Migration/Compatibility check
                    if (data.length > 0 && data[0].title && !data[0].albums) {
                        setGalleryAlbums([{ category: 'General Categories', albums: data }]);
                    } else {
                        setGalleryAlbums(data);
                    }
                } catch (e) {
                    console.error("Failed to parse gallery albums", e);
                }
            }
        } catch (err) {
            console.error("Failed to load web content", err);
        }
    };

    const resetView = () => {
        setCurrentCategory(null);
        setCurrentAlbum(null);
    };

    const backToCategory = () => {
        setCurrentAlbum(null);
    };

    return (
        <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
            {!isScrolled && <TopBar data={content} />}
            <Header 
                isScrolled={isScrolled} 
                onApply={() => {}}
                logo={content.site_logo}
                colleges={content.colleges_list}
                placements={content.placements_list}
                research={content.research_list}
                journals={content.journals_list}
                innovations={content.innovations_list}
                applyUrl={content.apply_url}
                email={content.contact_email}
                phone={content.contact_phone}
            />

            {/* Internal Hero */}
            <header className="relative h-[400px] bg-slate-900 flex items-center overflow-hidden">
                <div className="absolute inset-0 bg-[#003366]/85 z-10"></div>
                <img 
                    src="https://images.unsplash.com/photo-1541339907198-e08756ebafe3?q=80&w=2000&auto=format&fit=crop" 
                    className="absolute inset-0 w-full h-full object-cover"
                    alt="Gallery"
                />
                <div className="max-w-7xl mx-auto px-4 relative z-20 w-full">
                    <h4 className="text-amber-400 font-black uppercase tracking-[8px] text-xs mb-4">Campus Life</h4>
                    <h1 className="text-5xl md:text-7xl font-black text-white uppercase tracking-tighter">
                        Photo <span className="text-[#cc0000]">Gallery</span>
                    </h1>
                    <div className="h-2 w-24 bg-white mt-8"></div>
                </div>
            </header>

            {/* Gallery Section */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4">
                    {/* Breadcrumbs / Navigation */}
                    {(currentCategory || currentAlbum) && (
                        <div className="mb-12 flex flex-wrap items-center gap-4">
                            <button 
                                onClick={resetView}
                                className="text-[#cc0000] font-black uppercase text-xs tracking-widest hover:underline"
                            >
                                All Folders
                            </button>
                            {currentCategory && (
                                <>
                                    <span className="text-slate-300">/</span>
                                    <button 
                                        onClick={backToCategory}
                                        className={`font-black uppercase text-xs tracking-widest ${!currentAlbum ? 'text-slate-400' : 'text-[#cc0000] hover:underline'}`}
                                        disabled={!currentAlbum}
                                    >
                                        {currentCategory.category}
                                    </button>
                                </>
                            )}
                            {currentAlbum && (
                                <>
                                    <span className="text-slate-300">/</span>
                                    <span className="font-black uppercase text-xs tracking-widest text-slate-400">
                                        {currentAlbum.title}
                                    </span>
                                </>
                            )}
                        </div>
                    )}

                    {!currentCategory ? (
                        /* Categories (Years) View */
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                            {galleryAlbums.map((cat, idx) => (
                                <div 
                                    key={idx}
                                    onClick={() => setCurrentCategory(cat)}
                                    className="group cursor-pointer bg-white p-8 border-b-4 border-[#003366] hover:border-[#cc0000] transition-all shadow-lg hover:-translate-y-2"
                                >
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="h-[2px] w-8 bg-[#cc0000]"></div>
                                        <span className="text-[10px] font-black uppercase tracking-[4px] text-slate-400 group-hover:text-[#cc0000] transition-colors">Memory of</span>
                                    </div>
                                    <h3 className="text-4xl font-black uppercase tracking-tighter italic mb-6 leading-none">{cat.category}</h3>
                                    <div className="flex items-center justify-between">
                                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">{cat.albums?.length || 0} Events</span>
                                        <div className="w-8 h-[2px] bg-slate-200 group-hover:w-16 group-hover:bg-[#cc0000] transition-all"></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : !currentAlbum ? (
                        /* Albums (Events) View */
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 animate-in fade-in slide-in-from-bottom-4">
                            {currentCategory.albums.map((album, idx) => (
                                <div 
                                    key={idx}
                                    onClick={() => setCurrentAlbum(album)}
                                    className="group cursor-pointer"
                                >
                                    <div className="relative aspect-[4/5] overflow-hidden bg-slate-900 shadow-2xl border-b-8 border-[#cc0000]">
                                        <img 
                                            src={getFullUrl(album.cover || album.images?.[0])} 
                                            className="w-full h-full object-cover transform transition-transform duration-1000 group-hover:scale-110 opacity-70 group-hover:opacity-100"
                                            alt={album.title}
                                        />
                                        <div className="absolute inset-x-0 bottom-0 p-8 bg-gradient-to-t from-black/90 to-transparent">
                                            <span className="bg-white/10 backdrop-blur-md text-white text-[10px] font-black uppercase tracking-[4px] px-3 py-1 inline-block mb-3 border border-white/20">
                                                {album.images?.length || 0} Photos
                                            </span>
                                            <h3 className="text-white text-3xl font-black uppercase tracking-tighter leading-none italic">{album.title}</h3>
                                        </div>
                                    </div>
                                    <div className="mt-4 flex justify-between items-center opacity-0 group-hover:opacity-100 transition-opacity">
                                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">View Events</span>
                                        <div className="h-[2px] w-12 bg-[#cc0000]"></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        /* Images View */
                        <div className="columns-1 sm:columns-2 lg:columns-3 gap-8 space-y-8 animate-in fade-in zoom-in-95 duration-500">
                            {currentAlbum.images.map((img, idx) => (
                                <div 
                                    key={idx} 
                                    className="relative group overflow-hidden bg-white shadow-xl break-inside-avoid"
                                >
                                    <img 
                                        src={getFullUrl(img)} 
                                        alt={`${currentAlbum.title} ${idx}`} 
                                        className="w-full h-auto object-cover transform transition-transform duration-700 group-hover:scale-110"
                                        loading="lazy"
                                    />
                                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                        <button 
                                            onClick={() => setSelectedImage(img)}
                                            className="bg-[#cc0000] text-white p-4 rounded-full transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300"
                                        >
                                            <Maximize2 size={24} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Lightbox */}
            {selectedImage && (
                <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4">
                    <button 
                        onClick={() => setSelectedImage(null)}
                        className="absolute top-10 right-10 text-white hover:text-[#cc0000] transition-colors"
                    >
                        <X size={48} strokeWidth={1} />
                    </button>
                    <img 
                        src={getFullUrl(selectedImage)} 
                        alt="Selected Gallery" 
                        className="max-w-full max-h-[90vh] object-contain shadow-2xl"
                    />
                </div>
            )}

            <Footer data={content} />
        </div>
    );
};

export default Gallery;
